import axios from "axios";
import { getAnonimousUserCode } from "@/api/interactions";
import {
  IFrisbuyAnalyticsParams,
  IFrisbuyAnalyticsPropsParams,
} from "./interfaces";
import { AnalyticBuffer } from "../_ANALYTICS_SERVICES/AnalyticBuffer";

declare global {
  export interface Window {
    [key: string]: any;
  }
}

export function sendFrisbuyAnalyticsEvent(
  action: string,
  params: IFrisbuyAnalyticsPropsParams
) {
  const ENDPOINT = "/fb/widget/analytics";
  const srcDomain = params.srcDomain;
  const clientId = getAnonimousUserCode();

  const localStorageBufferKey = "frisbuyEventsBuffer";
  const localStorageBuffer = new AnalyticBuffer(localStorageBufferKey);

  const isDebugMode = window.location.search.search("_fb_debug") >= 0;

  const eventName = getEventName(action, "frisbuy_");
  if (!eventName) {
    return;
  }

  const FrisbuyAnalyticsParams: IFrisbuyAnalyticsParams = deleteEmptyFields({
    embedId: params.embedId,
    postId: params.fbProduct,
    linkId: params.link,
    linkLabel: params.linkLabel,

    widgetType: params.widgetType,
    domInject: params.domInject,
    queued: params.queued,
    load: params.load,
    bundle: params.bundle,
  });

  const FrisbuyAnalyticsRequestData = {
    eventName: eventName,
    clientId: clientId,
    os: navigator.userAgent,
    osType: navigator.platform,
    viewTitle: document.title,

    params: FrisbuyAnalyticsParams,
  };

  localStorageBuffer.push(FrisbuyAnalyticsRequestData);

  /*
    NOTE:
    This loop, like `forEach`, goes through all elements of the buffer.
    The only difference is `shift()` and `push()`.
    If you delete an array element using the shift method, all elements will shift to the left,
    and the search index will increase by 1 - accordingly, the next element after the deleted one
    will be skipped in `forEach()`.
    To return the index, use `i -= 1`. And `i += 1` to if on push element on error.
  */
  // TODO: refactor this loop. Make it more human readable
  for (let i = 0; i < localStorageBuffer.length; i++) {
    if (!localStorageBuffer.data[i]) {
      break;
    }

    const event = localStorageBuffer.shift();
    i -= 1;

    axios
      .post(srcDomain + ENDPOINT, event)
      .then(() => {
        if (isDebugMode) {
          verboseCallback(event.eventName, event);
        }
      })
      .catch((e) => {
        localStorageBuffer.push(event);
        i += 1;

        if (isDebugMode) {
          verboseCallback(event.eventName, event, e);
        }
      })
      .finally(() => {
        localStorageBuffer.setBufferToLocalStorage();
      });
  }
}

function verboseCallback(action: string, event: Object, e?: Error) {
  const logInfo = `%cFrisbuyAnalytics ${action}`;
  const stylesForLogs =
    "background-color: #0055ee; color: #ffffff; font-size: 16px; border-radius: 4px; padding: 4px 6px; font-weight: 400";

  const errorMarkChar = "%c!Error";
  const errorMarkStyles =
    "background-color: #ee0000; color: #ffffff; font-size: 16px; border-radius: 4px; padding: 4px 6px; font-weight: 400";

  if (e) {
    console.error(
      `${errorMarkChar}${logInfo}`,
      errorMarkStyles,
      stylesForLogs,
      event,
      e
    );
  } else {
    console.log(logInfo, stylesForLogs, event);
  }
}

function getEventName(action: string, actionPrefix: string) {
  switch (action) {
    case "Load":
      return actionPrefix + "load";
      break;
    case "Eyesight":
      return actionPrefix + "eyesight";
      break;
    case "Eyesight5Sec":
      return actionPrefix + "eyesight5sec";
      break;
    case "ClickPic":
      return actionPrefix + "clickpost";
      break;
    case "ViewPic":
      return actionPrefix + "viewpost";
      break;
    case "ClickLink":
      return actionPrefix + "clicklink";
      break;
    case "FilterElastic":
      return actionPrefix + "filterload";
      break;
    case "NextPage":
      return actionPrefix + "nextpage";
      break;
    case "PlayVideo":
      return actionPrefix + "playvideo";
      break;
    case "Swipe":
      return actionPrefix + "swipe";
      break;
    case "AddToCart":
      return actionPrefix + "addtocart";
      break;
    case "AddToFavorite":
      return "add_to_wishlist";
      break;
    case "RemoveFromFavorite":
      return actionPrefix + "removefromfavorite";
      break;

    // NOTE: additional events
    case "TimeLoad":
      return actionPrefix + "time_load";
      break;

    default:
      console.log(`switch DEFAULT - action - ${action} - NOT match any case!`);
      break;
  }
}

function deleteEmptyFields(object: { [key: string | number | symbol]: any }) {
  for (const field in object) {
    if (
      !object[field] &&
      typeof object[field] !== "number" &&
      typeof object[field] !== "boolean"
    ) {
      delete object[field];
    }
  }

  return object;
}